import React, { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { format, parseISO } from 'date-fns'
import { CallbackStatus, OrderStatus, PaymentMethod } from '../types/graphql-global-types'
import { Button, ButtonVariant } from './button'
import { CopyIcon, CheckIcon, EyeIcon, EyeSlashIcon, EmptyBoxIcon, SpinnerIcon } from './icons'

// ---------------------------------------------------------------------------
// Layout primitives
// ---------------------------------------------------------------------------

interface PageHeaderProps {
    title: string
    subtitle?: ReactNode
    children?: ReactNode // right-aligned actions
}

export function PageHeader({ title, subtitle, children }: PageHeaderProps): JSX.Element {
    return (
        <div className='my-6 lg:my-12 container px-6 mx-auto flex flex-col lg:flex-row items-start lg:items-center justify-between pb-4 border-b border-gray-300'>
            <div>
                <h4 className='text-2xl font-bold leading-tight text-gray-800'>{title}</h4>
                {subtitle ? <p className='text-gray-400 text-sm mt-2'>{subtitle}</p> : null}
            </div>
            {children ? (
                <div className='flex flex-col items-stretch w-full sm:w-auto sm:flex-row sm:items-center gap-3 mt-6 lg:mt-0'>
                    {children}
                </div>
            ) : null}
        </div>
    )
}

// A responsive, bordered table shell. Horizontal scroll on small screens keeps
// wide tables usable instead of crushing the columns.
export function TableCard({ children }: { children: ReactNode }): JSX.Element {
    return (
        <div className='container mx-auto px-6'>
            <div className='bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden'>
                <div className='overflow-x-auto'>
                    <table className='w-full'>{children}</table>
                </div>
            </div>
        </div>
    )
}

export function TableHead({ columns }: { columns: ReactNode[] }): JSX.Element {
    return (
        <thead className='bg-gray-50 border-b border-gray-200'>
            <tr className='text-left uppercase text-gray-500 text-xs tracking-wider'>
                {columns.map((c, i) => (
                    <th key={i} className='px-4 py-3 font-semibold whitespace-nowrap'>{c}</th>
                ))}
            </tr>
        </thead>
    )
}

// Full-width row used inside a tbody for loading / empty / error placeholders.
export function TableMessageRow({ colSpan, children }: { colSpan: number; children: ReactNode }): JSX.Element {
    return (
        <tr>
            <td colSpan={colSpan} className='px-4 py-16'>{children}</td>
        </tr>
    )
}

interface EmptyStateProps {
    title: string
    subtitle?: string
    icon?: React.ComponentType<{ size?: number; className?: string }>
    action?: ReactNode
}

export function EmptyState({ title, subtitle, icon: Icon = EmptyBoxIcon, action }: EmptyStateProps): JSX.Element {
    return (
        <div className='flex flex-col items-center justify-center text-center'>
            <Icon size={2.5} className='text-gray-300' />
            <p className='mt-4 text-sm font-medium text-gray-700'>{title}</p>
            {subtitle ? <p className='mt-1 text-sm text-gray-400 max-w-sm'>{subtitle}</p> : null}
            {action ? <div className='mt-5'>{action}</div> : null}
        </div>
    )
}

export function LoadingRows({ message }: { message?: string }): JSX.Element {
    const { t } = useTranslation()
    const label = message ?? t('shared.state.loading')
    return (
        <div className='flex items-center justify-center text-gray-400 text-sm gap-3'>
            <svg viewBox='0 0 24 24' width='1.25em' height='1.25em' fill='none' className='animate-spin'>
                <circle cx='12' cy='12' r='9' stroke='currentColor' strokeWidth={2.5} opacity={0.25} />
                <path d='M12 3a9 9 0 0 1 9 9' stroke='currentColor' strokeWidth={2.5} strokeLinecap='round' />
            </svg>
            {label}
        </div>
    )
}

// Centered card for create/edit forms.
export function FormCard({ children }: { children: ReactNode }): JSX.Element {
    return (
        <div className='container mx-auto px-6'>
            <div className='bg-white border border-gray-200 rounded-lg shadow-sm max-w-2xl p-6 sm:p-8'>
                {children}
            </div>
        </div>
    )
}

// Consistent input styling shared by every form control.
export const inputClassName = 'mt-1 block w-full px-3 py-2 rounded border border-gray-300 shadow-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'

export function Field({ label, children }: { label: string; children: ReactNode }): JSX.Element {
    return (
        <label className='block mb-5'>
            <span className='text-sm font-medium text-gray-700'>{label}</span>
            {children}
        </label>
    )
}

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------

type BadgeColor = 'gray' | 'green' | 'red' | 'amber' | 'indigo' | 'blue'

const badgeColors: Record<BadgeColor, string> = {
    gray: 'bg-gray-100 text-gray-600',
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
    amber: 'bg-amber-100 text-amber-700',
    indigo: 'bg-indigo-100 text-indigo-700',
    blue: 'bg-blue-100 text-blue-700',
}

export function Badge({ color, children }: { color: BadgeColor; children: ReactNode }): JSX.Element {
    return (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${badgeColors[color]}`}>
            {children}
        </span>
    )
}

const orderStatusColor: Record<OrderStatus, BadgeColor> = {
    [OrderStatus.created]: 'gray',
    [OrderStatus.waitingForPayment]: 'amber',
    [OrderStatus.paymentSuccess]: 'green',
    [OrderStatus.paymentFailed]: 'red',
    [OrderStatus.refunded]: 'indigo',
}

export function OrderStatusBadge({ status }: { status: OrderStatus }): JSX.Element {
    const { t } = useTranslation()
    return <Badge color={orderStatusColor[status] ?? 'gray'}>{t(`orders.status.${status}`)}</Badge>
}

const callbackStatusColor: Record<CallbackStatus, BadgeColor> = {
    [CallbackStatus.success]: 'green',
    [CallbackStatus.error]: 'red',
    [CallbackStatus.none]: 'gray',
}

export function CallbackStatusBadge({ status }: { status: CallbackStatus }): JSX.Element {
    const { t } = useTranslation()
    return <Badge color={callbackStatusColor[status] ?? 'gray'}>{t(`orders.callback.${status}`)}</Badge>
}

export function MethodBadge({ method }: { method: PaymentMethod }): JSX.Element {
    const { t } = useTranslation()
    return <Badge color='blue'>{t(`shared.method.${method}`)}</Badge>
}

// ---------------------------------------------------------------------------
// Date formatting
// ---------------------------------------------------------------------------

export function formatDateTime(value: string | null | undefined): string {
    if (!value) return '—'
    const date = typeof value === 'string' ? parseISO(value) : value
    if (isNaN(date.getTime())) return String(value)
    return format(date, 'dd MMM yyyy, HH:mm')
}

export function DateTime({ value }: { value: string | null | undefined }): JSX.Element {
    return <span className='text-gray-600 whitespace-nowrap'>{formatDateTime(value)}</span>
}

// ---------------------------------------------------------------------------
// Copy-to-clipboard helpers
// ---------------------------------------------------------------------------

function useCopied(): [boolean, (text: string) => void] {
    const [copied, setCopied] = useState(false)
    const copy = (text: string) => {
        void navigator.clipboard?.writeText(text)
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1500)
    }
    return [copied, copy]
}

export function CopyButton({ value, ariaLabel = 'Copy' }: { value: string; ariaLabel?: string }): JSX.Element {
    const [copied, copy] = useCopied()
    return (
        <button
            type='button'
            onClick={() => copy(value)}
            aria-label={copied ? 'Copied' : ariaLabel}
            title={copied ? 'Copied' : ariaLabel}
            className='text-gray-400 hover:text-indigo-600 focus:outline-none focus:text-indigo-600 transition'>
            {copied ? <CheckIcon size={0.9} className='text-green-500' /> : <CopyIcon size={0.9} />}
        </button>
    )
}

// Truncated, monospace identifier with a copy affordance.
export function MonoId({ value }: { value: string }): JSX.Element {
    return (
        <span className='inline-flex items-center gap-1.5'>
            <code className='font-mono text-xs text-gray-500' title={value}>{value.slice(0, 8)}…</code>
            <CopyButton value={value} ariaLabel='Copy ID' />
        </span>
    )
}

// Secret app key: masked by default, with reveal + copy.
export function SecretKey({ value }: { value: string }): JSX.Element {
    const [revealed, setRevealed] = useState(false)
    const masked = value.length > 4 ? `${'•'.repeat(8)}${value.slice(-4)}` : '••••••••'
    return (
        <span className='inline-flex items-center gap-1.5'>
            <code className='font-mono text-xs text-gray-600'>{revealed ? value : masked}</code>
            <button
                type='button'
                onClick={() => setRevealed((v) => !v)}
                aria-label={revealed ? 'Hide secret' : 'Reveal secret'}
                title={revealed ? 'Hide' : 'Reveal'}
                className='text-gray-400 hover:text-indigo-600 focus:outline-none focus:text-indigo-600 transition'>
                {revealed ? <EyeSlashIcon size={0.9} /> : <EyeIcon size={0.9} />}
            </button>
            <CopyButton value={value} ariaLabel='Copy secret' />
        </span>
    )
}

// ---------------------------------------------------------------------------
// Destructive confirmation
// ---------------------------------------------------------------------------

interface ConfirmButtonProps {
    onConfirm: () => void
    title: string
    message: ReactNode
    confirmLabel?: string
    cancelLabel?: string
    size?: 'sm' | 'md'
    variant?: ButtonVariant // trigger button style
    confirmVariant?: ButtonVariant // confirm button style in the dialog (defaults to variant)
    loading?: boolean // show a spinner on the trigger while the action runs
    children: ReactNode // trigger label
}

export function ConfirmButton({ onConfirm, title, message, confirmLabel, cancelLabel, size = 'sm', variant = 'danger', confirmVariant, loading = false, children }: ConfirmButtonProps): JSX.Element {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const confirm = confirmLabel ?? t('shared.button.label.delete')
    const cancel = cancelLabel ?? t('shared.button.label.cancel')
    return (
        <>
            <Button variant={variant} size={size} disabled={loading} onClick={() => setOpen(true)}>
                {loading ? <SpinnerIcon /> : children}
            </Button>
            {open ? (
                <div className='fixed inset-0 z-50 flex items-center justify-center p-4' role='dialog' aria-modal='true'>
                    <div className='absolute inset-0 bg-gray-900 bg-opacity-40' onClick={() => setOpen(false)} />
                    <div className='relative bg-white rounded-lg shadow-xl max-w-md w-full p-6'>
                        <h3 className='text-lg font-semibold text-gray-800'>{title}</h3>
                        <div className='mt-2 text-sm text-gray-500'>{message}</div>
                        <div className='mt-6 flex justify-end gap-3'>
                            <Button variant='secondary' onClick={() => setOpen(false)}>{cancel}</Button>
                            <Button variant={confirmVariant ?? variant} onClick={() => { setOpen(false); onConfirm() }}>{confirm}</Button>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    )
}
